import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as XLSX from 'xlsx-js-style';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { DataImporterService } from '../../../core/services/data-importer';
import { AlertService } from '../../../core/ui/alert';

@Component({
	selector: 'app-data-importer',
	standalone: true,
	imports: [CommonModule, FormsModule, MatButtonModule, MatFormFieldModule, MatSelectModule, MatInputModule],
	templateUrl: './data-importer.html',
	styleUrl: './data-importer.css'
})
export class DataImporter implements OnInit {
	selectedFile: File | null = null;

  selectedCollection: string = '';


  // Lista de colecciones que se pueden importar a través de este componente.

  collections = [

    { value: 'partners', viewValue: 'Clientes' },

    { value: 'entities', viewValue: 'Entidades' },

    { value: 'routes', viewValue: 'Rutas' },

    { value: 'buses', viewValue: 'Buses' },

    { value: 'fixedDiscountTypes', viewValue: 'Tipos de Descuento Fijo' },

    { value: 'variableDiscountTypes', viewValue: 'Tipos de Descuento Variable' },

    // Si se añade soporte para más colecciones, se deben listar aquí y actualizar el 'columnTypeMapping'.

  ];


  // DEFINICIÓN PRECISA PARA TIPOS DE COLUMNAS POR COLECCIÓN.

  // Mapea el nombre de la columna del Excel a su tipo de dato deseado en Firestore.

  // Los campos 'createdAt', 'updatedAt' y 'createdBy' son manejados automáticamente

  // por la Cloud Function y NO deben estar presentes en el archivo Excel.

  private columnTypeMapping: {

    [collection: string]: {

      strings?: string[],

      numbers?: string[],

      booleans?: string[],

      jsonStrings?: string[] // Para campos que son arrays o objetos y vienen como JSON stringificado en el Excel

      // No se identificaron campos de tipo 'Date' directos que necesiten conversión especial aquí,

      // la Cloud Function ya maneja timestamps de creación/actualización.

    }

  } = {

    'entities': {

      strings: [

        'name',

        'entityEmail',

        'legalRepresentative',

        'legalRepresentativeEmail'

      ],

      booleans: ['status']

    },


    'routes': {

      strings: [

        'name'

      ],

      booleans: ['status']

    },


    'partners': {

      strings: [

        'email',

        'displayName',

        'NIT'

      ],

      booleans: [

        'disabled',

        'paysISR'

      ],
    },


    'buses': {

      strings: [

        'name',         // CRÍTICO: el número o identificador del bus, siempre como string (ej. "001", "A-15")

        'licensePlate',

        'partnerId',

        'entityId',

        'routeId'

      ],

      booleans: ['status']

    },


    'fixedDiscountTypes': {

      strings: [

        'name',

        'applicationType' // 'global_weekly', 'global_monthly', 'by_entity_selected'

      ],

      numbers: ['amount'],

      booleans: ['status']

    },


    'variableDiscountTypes': {

      strings: [

        'name',

        'applicableTo' // 'entity', 'partner'

      ],

      booleans: ['status']

      // No se especificaron campos numéricos aquí, si existieran, se añadirían a 'numbers'.

    }

  };



  constructor(

    private dataImporterService: DataImporterService,

    private alertService: AlertService

  ) { }


  ngOnInit(): void { }


  onFileSelected(event: any): void {

    const file = event.target.files[0];

    if (file) {

      this.selectedFile = file;

    } else {

      this.selectedFile = null;

    }

  }


  async importExcelData(): Promise<void> {

    if (!this.selectedFile) {

      this.alertService.error('Por favor, seleccione un archivo Excel.', 'Error');

      return;

    }

    if (!this.selectedCollection) {

      this.alertService.error('Por favor, seleccione la colección de destino.', 'Error');

      return;

    }


    const reader = new FileReader();

    reader.onload = async (e: any) => {

      try {

        const data = new Uint8Array(e.target.result);

        const workbook = XLSX.read(data, { type: 'array', cellText: true });

        const sheetName = workbook.SheetNames[0];


        // OJO: Usamos { cellText: true } para obtener el valor visible de la celda como string.

        // Esto es crucial para preservar ceros iniciales (ej. "001" si la celda fue formateada como texto).

        // Sin embargo, si Excel ya quitó los ceros (celda "General" o "Número"), solo se leerá el valor numérico.

        let excelData: any[] = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);


        if (excelData.length === 0) {

          this.alertService.error('El archivo Excel está vacío o no contiene datos.', 'Advertencia');

          return;

        }


        // Obtener las reglas de conversión para la colección seleccionada

        const currentCollectionMapping = this.columnTypeMapping[this.selectedCollection];

        const fieldsToForceToString = currentCollectionMapping?.strings || [];

        const fieldsToForceToNumber = currentCollectionMapping?.numbers || [];

        const fieldsToForceToBoolean = currentCollectionMapping?.booleans || [];

        const fieldsToForceToJson = currentCollectionMapping?.jsonStrings || [];


        // Post-procesamiento para aplicar las conversiones de tipo según el mapeo definido

        excelData = excelData.map(row => {

          const newRow: { [key: string]: any } = {};

          for (const key in row) {

            if (Object.prototype.hasOwnProperty.call(row, key)) {

              const value = row[key];


              // Manejar celdas vacías o con solo espacios como null

              if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {

                newRow[key] = null;

              } else if (fieldsToForceToNumber.includes(key)) {

                // Convertir a número. Si no es un número válido, se establece como null.

                const numValue = Number(value);

                newRow[key] = isNaN(numValue) ? null : numValue;

              } else if (fieldsToForceToBoolean.includes(key)) {

                // Convertir a booleano. Acepta "true", "1", "sí", "yes", "false", "0", "no".

                const stringValue = String(value).toLowerCase().trim();

                if (stringValue === 'true' || stringValue === '1' || stringValue === 'sí' || stringValue === 'yes') {

                  newRow[key] = true;

                } else if (stringValue === 'false' || stringValue === '0' || stringValue === 'no') {

                  newRow[key] = false;

                } else {

                  newRow[key] = null; // Si no es un booleano claro, se guarda como null

                }

              } else if (fieldsToForceToJson.includes(key)) {

                 // Convertir a objeto/array desde una cadena JSON. Si es inválido, se guarda como null.

                 try {

                   newRow[key] = JSON.parse(String(value));

                 } catch (e) {

                   newRow[key] = null;

                 }

              } else if (fieldsToForceToString.includes(key)) {

                // Forzar a string para campos definidos como string.

                newRow[key] = String(value);

              } else {

                // Para cualquier otra columna no mapeada explícitamente, se mantiene el valor tal cual

                // fue leído por XLSX (normalmente un string debido a 'cellText: true').

                newRow[key] = value;

              }

            }

          }

          return newRow;

        });

        

        // Enviar datos a la Cloud Function en chunks para manejar grandes volúmenes.

        if (this.selectedCollection === 'partners') {

          // Chunk size para partners es más pequeño debido a la creación de Auth

          const partnerChunkSize = 50; 

          for (let i = 0; i < excelData.length; i += partnerChunkSize) {

            const chunk = excelData.slice(i, i + partnerChunkSize);

            await this.dataImporterService.importPartners(chunk); // <-- ¡AHORA LLAMA A importPartners!

          }

        } else {

          const genericChunkSize = 400; // Chunk size para otras colecciones

          for (let i = 0; i < excelData.length; i += genericChunkSize) {

            const chunk = excelData.slice(i, i + genericChunkSize);

            await this.dataImporterService.importData(this.selectedCollection, chunk); // <-- LLAMA A importData para el resto

          }

        }


        

        

        this.selectedFile = null; // Limpiar el archivo seleccionado tras la importación

      } catch (error: any) {

        console.error('Error al leer o importar el archivo Excel:', error);

        this.alertService.error('Error al procesar el archivo Excel: ' + error.message, 'Error de Procesamiento');

      }

    };

    reader.readAsArrayBuffer(this.selectedFile);

  }

}
